import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { toast } from 'src/components/snackbar'
import {
  containsNoValues,
  checkValidations,
  extractCodeOrInputText
} from 'src/libs/qb-brand-web-components'

import ManageAccounts from '../components/manageAccounts/ManageAccounts'
import {
  changeExchangeProvidersState,
  connectExchangeProvider,
  updateExchangeProvider,
  resetExchangeProvider
} from '../store/actions/exchange'
import { trackGTMDataLayer } from 'wrappers/trackGTMDataLayer'
import {
  exchangeProviderAccountLabel,
  filterExchangeProviderForManage
} from '../util/pointsExchange.helpers'
import { validationRulesForAccountNumberSanity } from '../util/validation.helpers'
import {
  EXCHANGE_PROVIDER_PROCESS_STEP,
  DEFAULT_CONTAINER_STATE,
  QR_CODE_PROCESS_STEP
} from '../constants/containerStates'

import CameraBlocked from '../components/popups/qrCode/CameraBlocked'
import ConnectAccountByQrCode from '../components/popups/qrCode/ConnectAccountByQrCode'
import { EXCHANGE_PROVIDER_PROFILE_DISPATCH } from '../store/reducers/exchange/exchangeProviders'

const ManageAccountsContainer = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()

  const exchangeMemberships = useSelector(
    (state) => state.mainReducer.mainProfile.exchangeMemberships
  )
  const exchangeProviderProfile = useSelector(
    (state) => state.exchangeReducer?.exchangeProviderProfile
  )
  const mainProfile = useSelector((state) => state.mainReducer.mainProfile)

  const [popupState, setPopupState] = useState(DEFAULT_CONTAINER_STATE.NONE)
  const [provider, setProvider] = useState(null)

  useEffect(() => {
    trackGTMDataLayer(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    return () => dispatch(resetExchangeProvider())
  }, [dispatch])

  useEffect(() => {
    if (exchangeProviderProfile?.accountSaveState) {
      handleAccountSaveState(exchangeProviderProfile.accountSaveState)
      setPopupState(exchangeProviderProfile.accountSaveState)
    }
  }, [exchangeProviderProfile?.accountSaveState])

  const exchangeWhitelist = useMemo(
    () => filterExchangeProviderForManage(mainProfile.exchangeWhitelist),
    [mainProfile.exchangeWhitelist]
  )

  const handleAccountSaveState = (state) => {
    if (!provider?.brandName) return

    const messages = {
      [EXCHANGE_PROVIDER_PROCESS_STEP.CONNECTED_ACCOUNT]: 'common.connected',
      [EXCHANGE_PROVIDER_PROCESS_STEP.CHANGED_ACCOUNT]: 'common.changed'
    }

    if (messages[state]) {
      toast.success(
        t('exchange.account-saved-success', {
          providerName: provider.brandName,
          label: t(messages[state])
        })
      )
    } else if (state === EXCHANGE_PROVIDER_PROCESS_STEP.WRONG_ACCOUNT_NUMBER) {
      wrongAccountNumberToast()
    }

    setProvider(null)
  }

  const wrongAccountNumberToast = () => {
    if (provider?.brandName) {
      toast.error(
        t('exchange.incorrect-account-number', {
          providerName: provider.brandName,
          fieldLabel: exchangeProviderAccountLabel(t, provider, true)
        })
      )
    }
  }

  const handleQrCodeClick = (provider) => {
    setProvider(provider)
    setPopupState(QR_CODE_PROCESS_STEP.SCAN_CODE)
  }

  const handleQrCodeError = () =>
    setPopupState(QR_CODE_PROCESS_STEP.CAMERA_BLOCKED)

  const handleQrCodeScan = (text) => {
    if (!text || exchangeProviderProfile?.isProcessing) return

    const accountNumber = extractCodeOrInputText(text)
    if (accountNumber)
      validateAndSaveAccountNumber(provider, accountNumber, true)
  }

  const validateAndSaveAccountNumber = useCallback(
    (provider, accountNumber, isQrScan = false) => {
      const errors = checkValidations({
        connectAccountNumber: {
          value: accountNumber,
          rules: validationRulesForAccountNumberSanity(provider.brandId)
        }
      })

      const membership = exchangeMemberships[provider.brandId]
      const isValid =
        containsNoValues(errors) &&
        membership?.membershipNumber !== accountNumber

      if (isValid) {
        membership
          ? dispatch(
              updateExchangeProvider(
                membership?.id,
                provider?.brandId,
                accountNumber
              )
            )
          : dispatch(connectExchangeProvider(provider.brandId, accountNumber))
      } else {
        isQrScan
          ? wrongAccountNumberToast()
          : dispatch(
              changeExchangeProvidersState(
                EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR,
                errors
              )
            )
      }
    },
    [dispatch, exchangeMemberships]
  )

  const resetError = useCallback(
    () =>
      dispatch(
        changeExchangeProvidersState(
          EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR,
          {}
        )
      ),
    [dispatch]
  )

  const handleSaveAccount = useCallback(
    (provider, value) => {
      setProvider(provider)
      resetError()
      validateAndSaveAccountNumber(provider, value)
    },
    [resetError, validateAndSaveAccountNumber]
  )

  const handleNoPopup = () => setPopupState(DEFAULT_CONTAINER_STATE.NONE)

  const renderPopup = () => {
    if (popupState === QR_CODE_PROCESS_STEP.SCAN_CODE) {
      return (
        <ConnectAccountByQrCode
          onCancel={handleNoPopup}
          onError={handleQrCodeError}
          onScan={handleQrCodeScan}
          t={t}
        />
      )
    }
    if (popupState === QR_CODE_PROCESS_STEP.CAMERA_BLOCKED) {
      return <CameraBlocked onCancel={handleNoPopup} t={t} />
    }
    return null
  }

  return (
    <>
      <ManageAccounts
        exchangeWhitelist={exchangeWhitelist}
        memberships={exchangeMemberships}
        selectedProvider={provider}
        onSaveAccount={handleSaveAccount}
        onQrCodeClick={handleQrCodeClick}
        onChange={resetError}
        exchangeProviderProfile={exchangeProviderProfile}
        t={t}
      />
      {renderPopup()}
    </>
  )
}

export default ManageAccountsContainer
